export const DEFAULT_FAN_IN_CAP = 4;
export const DEFAULT_FAN_OUT_CAP = 8;
export const DEFAULT_OVERALL_FAN_OUT_CAP = 20;
export const DEFAULT_UNKNOWN_MEDD_WEIGHT = 3;
export const VENDOR_GRADE_LEVELS = [1, 2, 3, 4, 5] as const;

/** Local (per-account) defaults by grade. All grades default to 8. */
export const DEFAULT_FAN_OUT_CAPS: Record<string, number> = {
  "1": DEFAULT_FAN_OUT_CAP,
  "2": DEFAULT_FAN_OUT_CAP,
  "3": DEFAULT_FAN_OUT_CAP,
  "4": DEFAULT_FAN_OUT_CAP,
  "5": DEFAULT_FAN_OUT_CAP,
};

export function normalizeFanOutCaps(
  raw?: Record<string, unknown> | null
): Record<string, number> {
  const caps = { ...DEFAULT_FAN_OUT_CAPS };
  if (!raw || typeof raw !== "object") return caps;
  VENDOR_GRADE_LEVELS.forEach(grade => {
    const value = Number(raw[String(grade)]);
    if (Number.isFinite(value) && value >= 1 && value <= 99) {
      caps[String(grade)] = Math.round(value);
    }
  });
  return caps;
}

export function normalizeFanInCap(raw: unknown): number {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 1 || n > 99) return DEFAULT_FAN_IN_CAP;
  return Math.round(n);
}

export function normalizeUnknownMeddWeight(raw: unknown): number {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 1 || n > 5) return DEFAULT_UNKNOWN_MEDD_WEIGHT;
  return Math.round(n);
}

export function normalizeOverallFanOutCap(raw: unknown): number {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 1 || n > 999)
    return DEFAULT_OVERALL_FAN_OUT_CAP;
  return Math.round(n);
}

export function resolveFanOutCap(
  caps: Record<string, number> | null | undefined,
  grade: number | null | undefined
): number {
  const n = Number(grade);
  if (!Number.isFinite(n)) return DEFAULT_FAN_OUT_CAP;
  const cap = Number(caps?.[String(Math.round(n))]);
  return Number.isFinite(cap) && cap >= 1 ? cap : DEFAULT_FAN_OUT_CAP;
}

/**
 * Local + portfolio capacity for one owner.
 * Local follows grade, or DEFAULT_FAN_OUT_CAP (8) when grade is missing.
 * roleName is accepted for call-site compatibility and is not used.
 * Portfolio is always overallFanOutCap (default 20).
 */
export function resolveOwnerCapacities(options: {
  fanOutCaps?: Record<string, number> | null;
  overallFanOutCap?: number | null;
  grade?: number | null;
  roleName?: string | null;
}): { local: number; global: number } {
  const global = normalizeOverallFanOutCap(options.overallFanOutCap);
  const grade = Number(options.grade);
  if (!Number.isFinite(grade)) {
    return { local: DEFAULT_FAN_OUT_CAP, global };
  }
  return {
    local: resolveFanOutCap(options.fanOutCaps, grade),
    global,
  };
}

export function fanCapTone(count: number, cap: number): "ok" | "warn" | "over" {
  if (!cap || cap <= 0) return "ok";
  const pct = (Number(count) || 0) / cap;
  if (pct >= 1) return "over";
  if (pct >= 0.8) return "warn";
  return "ok";
}

export function fanCapColor(tone: "ok" | "warn" | "over"): string {
  if (tone === "over") return "var(--red)";
  if (tone === "warn") return "var(--amber)";
  return "var(--green)";
}
